import "../../../../css/GlobalStyles.css";
import { useCallback, useMemo, useState } from "react";
import {
  SavedFormQuestion,
  SavedFormQuestionState,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import RectangleButtonCreatorPanel from "../../../../components/Buttons/RectangleButtonCreatorPanel";
import MarkitPlusActionButton from "../../../../components/Subscription/MarkitPlusActionButton";
import CreatorModePanelWrapper from "../../../../components/CreatorDashboard/CreatorModePanelWrapper";
import SearchBoxContainer from "../../../../components/Containers/SearchBoxContainer";
import filter from "lodash.filter";
import { EmptyStateFlatlist } from "../../../../components/EmptyStates/EmptyStateFlatlist";
import StandardListContainer from "../../../../components/Containers/StandardListContainer";
import FlatlistLoadMore from "../../../../components/FlatlistLoadMore";
import FormBuilder from "../../../../components/Form/FormBuilder";
import { FormSavedQuestionsType } from "../../../../components/Form/FormSavedQuestions";
import DataQuestionItem from "../../../../components/DisplayItem/DataQuestion/DataQuestionItem";
import EmptyStateButton from "../../../../components/Buttons/EmptyStateButton";
import SavedQuestionTypeDropdown, {
  DataQuestionFilterType,
} from "../../../../components/Form/SavedQuestionTypeDropdown";
import { getSavedFormQuestionsToDisplay } from "../../../../utils/eventUtils/formUtils";
import { useLoadDataQuestionNumResponses } from "../../../../hooks/useLoadDataQuestionNumResponses";
import useAsyncOnMount from "../../../../hooks/useAsyncEffectOnMount";

const DataQuestionsPanel = () => {
  const { appInitialized, savedQuestions } =
    useSelector(getAccountState).account;
  const [value, setValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterState, setFilterState] = useState<DataQuestionFilterType>(
    DataQuestionFilterType.ANY_TYPE
  );
  const [formQuestionVisible, setFormQuestionVisible] = useState(false);
  const [selectedFormQuestion, setSelectedFormQuestion] =
    useState<SavedFormQuestion>();

  const styles = {
    container: {
      backgroundColor: Colors.WHITE,
      paddingInline: 30,
      paddingTop: 30,
      height: "100%",
    },
  };

  const {
    savedQuestionsToShow,
    savedQuestionDetails,
    isLoading,
    loadSavedQuestionDetails,
  } = useLoadDataQuestionNumResponses();

  useAsyncOnMount(async () => {
    await loadSavedQuestionDetails();
  });

  const contains = useCallback((question: SavedFormQuestion, query: string) => {
    const label = question.formInput.label ?? "";
    return label.toLowerCase().includes(query);
  }, []);

  const handleSearch = (text: string) => {
    setSearchTerm(text.toLowerCase());
  };

  const savedQuestionsToDisplay = useMemo(() => {
    let questions = getSavedFormQuestionsToDisplay(
      savedQuestionsToShow,
      value === 0
        ? SavedFormQuestionState.ACTIVE
        : SavedFormQuestionState.INACTIVE,
      filterState
    );
    if (searchTerm !== "") {
      questions = filter(questions, (question: SavedFormQuestion) => {
        return contains(question, searchTerm);
      });
    }
    return questions;
  }, [contains, filterState, savedQuestionsToShow, searchTerm, value]);

  const renderEmptyView = useMemo(() => {
    return (
      <EmptyStateFlatlist
        searchTerm={searchTerm}
        isLoading={false}
        containerStyles={{ paddingTop: 150 }}
        nonSearchEmptyView={
          filterState === DataQuestionFilterType.ANY_TYPE ? (
            <EmptyStateButton
              title={
                value === 0 ? "No Active Questions" : "No Inactive Questions"
              }
              description={
                savedQuestions.length === 0
                  ? "Create your first data question to start collecting data"
                  : ""
              }
              icon={<Icon icon={"mdi:database"} height={60} />}
              iconBox={70}
              containerStyles={{ paddingTop: 200 }}
            />
          ) : (
            <EmptyStateButton
              title={"No Results Found"}
              description={
                "There are no data questions found within this filter"
              }
              icon={
                <Icon
                  icon={"ion:search"}
                  height={60}
                  style={{ color: Colors.BLACK }}
                />
              }
              iconBox={70}
              containerStyles={{ paddingTop: 200 }}
            />
          )
        }
      />
    );
  }, [filterState, savedQuestions.length, searchTerm, value]);

  const renderItem = useCallback(
    (item: SavedFormQuestion) => {
      const foundSavedQuestionDetails = savedQuestionDetails.get(item.id);
      return (
        <DataQuestionItem
          key={item.id}
          item={item}
          numResponses={foundSavedQuestionDetails || 0}
          editOnPress={() => {
            setFormQuestionVisible(true);
            setSelectedFormQuestion(item);
          }}
        />
      );
    },
    [savedQuestionDetails]
  );

  return (
    <CreatorModePanelWrapper
      title="Data Questions"
      headerRight={
        <div className="AlignedRow" style={{ gap: 14 }}>
          <MarkitPlusActionButton />
          <RectangleButtonCreatorPanel
            title="New Question"
            iconName="ion:add-circle"
            onPress={() => {
              setSelectedFormQuestion(undefined);
              setFormQuestionVisible(true);
            }}
          />
        </div>
      }
      headerTabs={{
        tabNames: ["Active", "Inactive"],
        tabValue: value,
        setTabValue: setValue,
      }}
    >
      <div style={styles.container}>
        <StandardListContainer
          searchComp={
            <div className="AlignedRow" style={{ gap: 14 }}>
              <SearchBoxContainer
                placeholder={"Search"}
                onChange={(e) => handleSearch(e.target.value)}
                containerStyles={{ marginTop: 0, width: 394 }}
              />
              <SavedQuestionTypeDropdown
                selectedType={filterState}
                setSelectedType={setFilterState}
              />
            </div>
          }
          isLoading={!appInitialized || isLoading}
          listComp={
            <div className="ColumnNormal">
              <FlatlistLoadMore
                fullList={savedQuestionsToShow}
                currentList={savedQuestionsToDisplay}
                renderItem={renderItem}
                renderWhenEmpty={renderEmptyView}
                isLoadingMore={isLoading}
                showLoadMore={false}
                loadMoreOnPress={async () => {
                  await loadSavedQuestionDetails();
                }}
              />
            </div>
          }
        />
        {formQuestionVisible ? (
          <FormBuilder
            type={FormSavedQuestionsType.DATA}
            selectedQuestions={[]}
            setSelectedQuestions={() => {}}
            setIsFormsModalOpen={setFormQuestionVisible}
            formQuestionItem={selectedFormQuestion}
            setFormQuestionItem={setSelectedFormQuestion}
          />
        ) : null}
      </div>
    </CreatorModePanelWrapper>
  );
};

export default DataQuestionsPanel;
